/**
 * Admin Resource Factory
 * Creates standard CRUD service objects for Content Manager API endpoints
 */

import { contentManagerService } from './content-manager.service';
import { getDocumentId, convertOptions, convertResponse, cleanDataForSubmit } from './admin-resource-helpers';

// Safety cap so a runaway collection can't trigger unbounded requests
const MAX_PAGES = 100;

/**
 * Fetch every page of a collection so no records are hidden from lists.
 * The first request reveals pageCount; remaining pages load in parallel.
 */
export async function fetchAllPages(endpoint: string, cmOptions: any): Promise<any> {
  const first: any = await contentManagerService.getAll(endpoint, cmOptions);
  const pagination = first?.pagination;
  if (!pagination || !pagination.pageCount || pagination.pageCount <= 1) return first;

  const pages: number[] = [];
  for (let p = 2; p <= Math.min(pagination.pageCount, MAX_PAGES); p++) pages.push(p);
  const rest = await Promise.all(
    pages.map((page) => contentManagerService.getAll(endpoint, { ...cmOptions, page }))
  );
  return {
    ...first,
    results: [
      ...(first.results || []),
      ...rest.flatMap((r: any) => r.results || []),
    ],
  };
}

export function createAdminResource(endpoint: string) {
  return {
    getAll: async (options?: any) => {
      const cmOptions = convertOptions(options);
      // Callers that request a specific page get true server-side pagination;
      // everyone else transparently receives ALL records across pages.
      const response = options?.pagination?.page !== undefined
        ? await contentManagerService.getAll(endpoint, cmOptions)
        : await fetchAllPages(endpoint, cmOptions);
      return convertResponse(response, endpoint);
    },
    getById: (id: number | string, options?: any) =>
      contentManagerService.getById(endpoint, id, options) as Promise<any>,
    create: (data: any) => {
      const cleanedData = cleanDataForSubmit(data, endpoint, false);
      return contentManagerService.create(endpoint, cleanedData) as Promise<any>;
    },
    update: (id: number | string, data: any) => {
      const documentId = getDocumentId(id, data);
      const cleanedData = cleanDataForSubmit(data, endpoint, true);
      return contentManagerService.update(endpoint, documentId, cleanedData) as Promise<any>;
    },
    delete: (id: number | string) => {
      const documentId = getDocumentId(id);
      return contentManagerService.delete(endpoint, documentId);
    },
  };
}

/**
 * Single types (website-hero-setting, erp-setting…) have no collection rows —
 * the Content Manager exposes them at /single-types/{uid} and PUT upserts.
 * Exposes the same service shape; getAll wraps the record in an array.
 */
export function createAdminSingleResource(endpoint: string) {
  const readRecord = async () => {
    const res: any = await contentManagerService.getSingle(endpoint);
    return res?.data ?? res;
  };
  return {
    getAll: async (_options?: any) => {
      const record = await readRecord();
      return { data: record && record.documentId ? [record] : [] };
    },
    getById: (_id?: number | string, _options?: any) => contentManagerService.getSingle(endpoint) as Promise<any>,
    create: (data: any) =>
      contentManagerService.updateSingle(endpoint, cleanDataForSubmit(data, endpoint, false)) as Promise<any>,
    update: (_id: number | string, data: any) =>
      contentManagerService.updateSingle(endpoint, cleanDataForSubmit(data, endpoint, true)) as Promise<any>,
    delete: (_id: number | string) => Promise.resolve(),
  };
}
